import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import Seo from "../components/Seo.jsx";
import GoogleLoginButton from "../components/GoogleLoginButton.jsx";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { access } = await login({ email, password });
      toast.success("Welcome back!");
      nav(access?.is_active ? "/dashboard" : "/paywall");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brimi-cream flex items-center justify-center px-5">
      <Seo
        title="Log in — BrimiH&G ICSE 2028 Study App"
        description="Log in to BrimiH&G to continue your ICSE 2028 prep — AI chapter notes, MCQ practice, mock tests, and the India + World map quiz for Class IX and X."
        path="/login"
      />
      <div className="w-full max-w-md brimi-card p-8">
        <Link to="/" className="font-heading font-bold text-xl text-brimi-navy block mb-1" data-testid="brand-link">
          Brimi<span className="text-brimi-terracotta">H</span>&<span className="text-brimi-sage">G</span>
        </Link>
        <h1 className="text-2xl font-heading font-semibold text-brimi-navyText mt-3">Welcome back</h1>
        <p className="text-sm text-brimi-navyText/70 mt-1">
          Log in to continue your ICSE Class IX-X History, Civics and Geography preparation — pick up where
          you left off in chapter notes, MCQ practice, the AI tutor chat, mock tests and the India + World
          map quiz.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <GoogleLoginButton label="Continue with Google" />
          <div className="flex items-center gap-3 my-1">
            <div className="h-px bg-brimi-creamSoft flex-1" />
            <span className="text-xs uppercase tracking-widest text-brimi-navyText/50 font-semibold">or</span>
            <div className="h-px bg-brimi-creamSoft flex-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/70">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/70">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
              data-testid="login-password-input"
            />
          </div>
          <button type="submit" disabled={loading} className="brimi-btn-primary w-full" data-testid="login-submit-btn">
            {loading ? "Logging in…" : "Log in"}
          </button>
        </form>
        <p className="text-sm text-brimi-navyText/70 mt-5 text-center">
          New here?{" "}
          <Link to="/signup" className="text-brimi-navy font-semibold hover:underline" data-testid="goto-signup-link">
            Create an account
          </Link>
        </p>
        <div className="mt-6 pt-5 border-t border-brimi-creamSoft">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60 mb-2">
            What you&apos;re logging back into
          </p>
          <ul className="text-sm text-brimi-navyText/75 space-y-1.5">
            <li>• AI-generated chapter notes &amp; PDF download for every ICSE 2028 chapter</li>
            <li>• 10 fresh MCQs per chapter with instant scoring</li>
            <li>• Claude-powered AI tutor with voice input &amp; read-aloud</li>
            <li>• 20-question timed mock tests with chapter-wise breakdown</li>
            <li>• India + World tap-to-place map quiz on real GeoJSON outlines</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
